import React, { useCallback, useState } from 'react';
import { Calendar, BarChart2, Save, RefreshCw, Download, Briefcase } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import DateInputSection from './DateInputSection';
import OperationSection from './OperationSection';
import RegionalSettingsSection from './RegionalSettingsSection';

// Импорт утилитарных функций
import { isInteger } from '../../../../../utils/validation';
import { addDays, addWorkingDays, formatDate } from '../../../../../utils/calculations/date';

/**
 * Компонент основной формы расчета дат
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  russianRegions,
  timeUnits,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Если включили или выключили учет рабочих дней
      if (name === 'includeWorkingDays') {
        if (checked && prev.unit !== 'workingDays') {
          // Сохраняем предыдущую единицу для возможного возврата
          newState._prevUnit = prev.unit;
          newState.unit = 'workingDays';
        } else if (!checked && prev.unit === 'workingDays') {
          // Возвращаем предыдущую единицу или устанавливаем "дни" по умолчанию
          newState.unit = prev._prevUnit || 'days';
        }
      }

      // Если изменили единицу на "рабочие дни", включаем соответствующую опцию
      if (name === 'unit' && value === 'workingDays' && !prev.includeWorkingDays) {
        newState.includeWorkingDays = true;
      }
      
      // Если выключили учет праздников, удаляем выбранный регион
      if (name === 'includeHolidays' && !checked) {
        newState.region = 'default';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      startDate, mode, unit, value, includeWorkingDays
    } = formData;

    // Валидация даты
    if (!startDate) {
      newErrors.startDate = 'Укажите начальную дату.';
    } else {
      try {
        const dateObj = new Date(startDate);
        if (isNaN(dateObj.getTime())) {
          newErrors.startDate = 'Некорректная дата.';
        }
      } catch (e) {
        newErrors.startDate = 'Некорректная дата.';
      }
    }

    // Валидация числового значения
    if (!value || value.trim() === '') {
      newErrors.value = 'Укажите количество.';
    } else if (!isInteger(value) || parseInt(value) < 0) {
      newErrors.value = 'Введите целое положительное число.';
    } else if (parseInt(value) > 10000) {
      newErrors.value = 'Максимальное значение - 10000.';
    }

    // Валидация режима
    if (!mode || !['add', 'subtract'].includes(mode)) {
      newErrors.mode = 'Выберите операцию.';
    }

    // Валидация единицы измерения
    if (!unit) {
      newErrors.unit = 'Выберите единицу измерения.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета
  const calculateDate = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const startDateObj = new Date(formData.startDate);
        const value = parseInt(formData.value, 10);
        const mode = formData.mode;
        const unit = formData.unit;
        const includeWorkingDays = formData.includeWorkingDays || unit === 'workingDays';
        const includeHolidays = formData.includeHolidays;
        const region = formData.region;
        const returnFormat = formData.returnFormat;
        const showWeekday = formData.showWeekday;

        // Инициализируем переменные для расчета
        let resultDate;
        let holidays = [];

        // Получаем праздники для выбранного региона
        if (includeHolidays) {
          // В реальном приложении здесь будет запрос к API или загрузка праздников из БД
          // Для демонстрации используем предопределенный список общероссийских праздников
          const currentYear = startDateObj.getFullYear();
          const nextYear = currentYear + 1;
          
          // Общероссийские праздники на текущий и следующий год
          holidays = [
            // Текущий год
            new Date(currentYear, 0, 1),  // Новый год
            new Date(currentYear, 0, 2),  // Новогодние каникулы
            new Date(currentYear, 0, 3),  // Новогодние каникулы
            new Date(currentYear, 0, 4),  // Новогодние каникулы
            new Date(currentYear, 0, 5),  // Новогодние каникулы
            new Date(currentYear, 0, 6),  // Новогодние каникулы
            new Date(currentYear, 0, 7),  // Рождество Христово
            new Date(currentYear, 0, 8),  // Новогодние каникулы
            new Date(currentYear, 1, 23), // День защитника Отечества
            new Date(currentYear, 2, 8),  // Международный женский день
            new Date(currentYear, 4, 1),  // Праздник Весны и Труда
            new Date(currentYear, 4, 9),  // День Победы
            new Date(currentYear, 5, 12), // День России
            new Date(currentYear, 10, 4), // День народного единства
            
            // Следующий год
            new Date(nextYear, 0, 1),     // Новый год
            new Date(nextYear, 0, 2),     // Новогодние каникулы
            new Date(nextYear, 0, 3),     // Новогодние каникулы
            new Date(nextYear, 0, 4),     // Новогодние каникулы
            new Date(nextYear, 0, 5),     // Новогодние каникулы
            new Date(nextYear, 0, 6),     // Новогодние каникулы
            new Date(nextYear, 0, 7),     // Рождество Христово
            new Date(nextYear, 0, 8),     // Новогодние каникулы
          ];
          
          // Добавляем региональные праздники
          if (region === 'tatarstan') {
            holidays.push(
              new Date(currentYear, 5, 25), // Ураза-байрам
              new Date(currentYear, 7, 30)  // Курбан-байрам
            );
          } else if (region === 'crimea') {
            holidays.push(new Date(currentYear, 2, 18)); // День воссоединения Крыма с Россией
          }
          
          // Добавление пользовательских праздников
          if (formData.customHolidays && formData.customHolidays.length > 0) {
            holidays = [...holidays, ...formData.customHolidays.map(date => new Date(date))];
          }
        }

        // Расчет даты
        if (includeWorkingDays || unit === 'workingDays') {
          // Если используем рабочие дни
          const effectiveValue = mode === 'add' ? value : -value;
          resultDate = addWorkingDays(startDateObj, effectiveValue, holidays);
        } else {
          // Обычный расчет дат
          let daysToAdd = 0;
          
          switch (unit) {
            case 'days':
              daysToAdd = value;
              break;
            case 'weeks':
              daysToAdd = value * 7;
              break;
            case 'months':
              resultDate = new Date(startDateObj);
              resultDate.setMonth(
                resultDate.getMonth() + (mode === 'add' ? value : -value)
              );
              break;
            case 'years':
              resultDate = new Date(startDateObj);
              resultDate.setFullYear(
                resultDate.getFullYear() + (mode === 'add' ? value : -value)
              );
              break;
            default:
              daysToAdd = value;
          }
          
          if (unit === 'days' || unit === 'weeks') {
            resultDate = addDays(startDateObj, mode === 'add' ? daysToAdd : -daysToAdd);
          }
        }

        // Форматируем даты
        const startFormatted = formatDate(startDateObj, returnFormat, showWeekday ? 'full' : returnFormat);
        const resultFormatted = formatDate(resultDate, returnFormat, showWeekday ? 'full' : returnFormat);
        
        // Вычисляем день недели
        const weekdays = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
        const startWeekday = weekdays[startDateObj.getDay()];
        const resultWeekday = weekdays[resultDate.getDay()];
        
        // Проверяем, является ли результат выходным днем
        const isWeekend = resultDate.getDay() === 0 || resultDate.getDay() === 6;
        
        // Проверяем, является ли результат праздничным днем
        const isHoliday = holidays.some(holiday => 
          holiday.getDate() === resultDate.getDate() && 
          holiday.getMonth() === resultDate.getMonth() && 
          holiday.getFullYear() === resultDate.getFullYear()
        );

        // Расчет разницы в днях
        const diffTime = Math.abs(resultDate - startDateObj);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Возвращаем результаты
        const finalResults = {
          startDate: startDateObj,
          resultDate: resultDate,
          startFormatted,
          resultFormatted,
          startWeekday,
          resultWeekday,
          diffDays,
          operation: {
            mode,
            value: parseInt(value),
            unit
          },
          isWeekend,
          isHoliday,
          includeWorkingDays,
          holidays
        };

        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      startDate: new Date().toISOString().split('T')[0],
      mode: 'add',
      unit: 'days',
      value: '30',
      includeWorkingDays: false,
      includeHolidays: false,
      region: 'default',
      customHolidays: [],
      returnFormat: 'full',
      showWeekday: true
    });
    setResults(null);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDate();
  };

  // Экспорт результатов в JSON
  const exportToJSON = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const exportData = {
      calculation: {
        input: { ...formData },
        results: {
          startDate: results.startDate.toISOString(),
          resultDate: results.resultDate.toISOString(),
          startFormatted: results.startFormatted,
          resultFormatted: results.resultFormatted,
          diffDays: results.diffDays,
          isWeekend: results.isWeekend,
          isHoliday: results.isHoliday
        }
      },
      exportDate: new Date().toISOString(),
      tool: "date-calculator"
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_даты.json');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-6 h-6 text-cyan-500" />
          Параметры расчета даты
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры даты */}
        <DateInputSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры операции */}
        <OperationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          timeUnits={timeUnits}
        />

        {/* Региональные настройки */}
        <RegionalSettingsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          russianRegions={russianRegions}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать дату'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToJSON}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт (JSON)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;